/**
 * Alarm Service - API communication for alarm management
 */

import {
  SCADAAlarm, AlarmDefinition, AlarmNotificationGroup, AlarmNotification,
  CreateAlarmDefinition, CreateAlarmNotificationGroup, AlarmAcknowledgment,
  AlarmClearRequest, AlarmFilter, AlarmListResponse, AlarmDashboard
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Global CSRF token cache
let csrfToken: string | null = null;

/**
 * Get CSRF token from backend
 */
const getCSRFToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.warn('Failed to get CSRF token:', error);
  }
  
  return '';
};

/**
 * Generic API request helper
 */
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add CSRF token for non-GET requests
  if (options.method && options.method !== 'GET') {
    const token = await getCSRFToken();
    if (token) {
      defaultHeaders['X-CSRFToken'] = token;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }

  return await response.json();
};

// Enhanced alarm management API methods

// Helper function to build query string from params
const buildQueryString = (params?: any): string => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// Alarm Definition API
export const alarmDefinitionsAPI = {
  // Basic CRUD operations
  getAll: (params?: any) => apiRequest(`/sites/alarm-definitions/${buildQueryString(params)}`),
  getById: (id: number) => apiRequest(`/sites/alarm-definitions/${id}/`),
  create: (data: CreateAlarmDefinition) => apiRequest('/sites/alarm-definitions/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: Partial<CreateAlarmDefinition>) => apiRequest(`/sites/alarm-definitions/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/sites/alarm-definitions/${id}/`, {
    method: 'DELETE',
  }),
  
  // Custom actions
  getActiveAlarms: (id: number) => apiRequest(`/sites/alarm-definitions/${id}/active_alarms/`),
  testConditions: (id: number) => apiRequest(`/sites/alarm-definitions/${id}/test_conditions/`, {
    method: 'POST',
  }),
  getSummary: () => apiRequest('/sites/alarm-definitions/summary/'),
  bulkUpdatePriority: (definitionIds: number[], priority: string) => 
    apiRequest('/sites/alarm-definitions/bulk_update_priority/', {
      method: 'POST',
      body: JSON.stringify({ definition_ids: definitionIds, priority }),
    }),
};

// Alarm Notification Group API
export const alarmNotificationGroupsAPI = {
  // Basic CRUD operations
  getAll: (params?: any) => apiRequest(`/sites/alarm-notification-groups/${buildQueryString(params)}`),
  getById: (id: number) => apiRequest(`/sites/alarm-notification-groups/${id}/`),
  create: (data: CreateAlarmNotificationGroup) => apiRequest('/sites/alarm-notification-groups/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: Partial<CreateAlarmNotificationGroup>) => apiRequest(`/sites/alarm-notification-groups/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/sites/alarm-notification-groups/${id}/`, {
    method: 'DELETE',
  }),
  
  // Custom actions
  getUsers: (id: number) => apiRequest(`/sites/alarm-notification-groups/${id}/users/`),
  addUsers: (id: number, userIds: number[]) => apiRequest(`/sites/alarm-notification-groups/${id}/add_users/`, {
    method: 'POST',
    body: JSON.stringify({ user_ids: userIds }),
  }),
  removeUsers: (id: number, userIds: number[]) => apiRequest(`/sites/alarm-notification-groups/${id}/remove_users/`, {
    method: 'POST',
    body: JSON.stringify({ user_ids: userIds }),
  }),
  testNotifications: (id: number, testMessage?: string) => 
    apiRequest(`/sites/alarm-notification-groups/${id}/test_notifications/`, {
      method: 'POST',
      body: JSON.stringify({ test_message: testMessage }),
    }),
  getSummary: () => apiRequest('/sites/alarm-notification-groups/summary/'),
};

// Alarm Notification API (read-only)
export const alarmNotificationsAPI = {
  getAll: (params?: any) => apiRequest(`/sites/alarm-notifications/${buildQueryString(params)}`),
  getById: (id: number) => apiRequest(`/sites/alarm-notifications/${id}/`),
  getDeliveryStats: () => apiRequest('/sites/alarm-notifications/delivery_stats/'),
};

// Enhanced SCADA Alarm API
export const scadaAlarmsAPI = {
  // Basic CRUD operations
  getAll: (params?: any) => apiRequest(`/sites/scada-alarms/${buildQueryString(params)}`),
  getById: (id: number) => apiRequest(`/sites/scada-alarms/${id}/`),
  create: (data: any) => apiRequest('/sites/scada-alarms/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: any) => apiRequest(`/sites/scada-alarms/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/sites/scada-alarms/${id}/`, {
    method: 'DELETE',
  }),
  
  // Enhanced actions
  acknowledge: (id: number, data: AlarmAcknowledgment) => apiRequest(`/sites/scada-alarms/${id}/acknowledge/`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  clearLocal: (id: number, data: AlarmClearRequest) => apiRequest(`/sites/scada-alarms/${id}/clear_local/`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  clearRemote: (id: number, data: AlarmClearRequest) => apiRequest(`/sites/scada-alarms/${id}/clear_remote/`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  clearBoth: (id: number, data: AlarmClearRequest) => apiRequest(`/sites/scada-alarms/${id}/clear_both/`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  suppress: (id: number, data: { reason?: string; suppressed_until?: string | null }) =>
    apiRequest(`/sites/scada-alarms/${id}/suppress/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  unsuppress: (id: number) =>
    apiRequest(`/sites/scada-alarms/${id}/unsuppress/`, {
      method: 'POST',
    }),
  
  // Clear method for backward compatibility
  clear: (data: AlarmClearRequest) => apiRequest('/sites/scada-alarms/clear/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Bulk operations
  bulkAcknowledge: (alarmIds: number[], comment?: string) => 
    apiRequest('/sites/scada-alarms/bulk-acknowledge/', {
      method: 'POST',
      body: JSON.stringify({ alarm_ids: alarmIds, comment }),
    }),
  bulkClear: (alarmIds: number[], clearType: string, comment?: string) => 
    apiRequest('/sites/scada-alarms/bulk-clear/', {
      method: 'POST',
      body: JSON.stringify({ alarm_ids: alarmIds, clear_type: clearType, comment }),
    }),
  
  // Dashboard and statistics
  getDashboard: (params?: any) => apiRequest(`/sites/scada-alarms/dashboard/${buildQueryString(params)}`),
  getActiveSummary: () => apiRequest('/sites/scada-alarms/active_summary/'),
  getStatistics: (days?: number) => apiRequest(`/sites/scada-alarms/statistics/${buildQueryString({ days })}`),
  
  // Legacy actions (for backward compatibility)
  getCurrentValue: (id: number) => apiRequest(`/sites/scada-alarms/${id}/current_value/`),
  getHistoricalData: (id: number, params?: any) => apiRequest(`/sites/scada-alarms/${id}/historical_data/${buildQueryString(params)}`),
};

// Testing API endpoints
export const testEmailServiceAPI = {
  testConnection: (emailConfig: any) => apiRequest('/sites/test-email-connection/', {
    method: 'POST',
    body: JSON.stringify(emailConfig),
  }),
  testNotification: (notificationGroupId: number, testMessage?: string) => 
    apiRequest('/sites/test-alarm-notification/', {
      method: 'POST',
      body: JSON.stringify({ notification_group_id: notificationGroupId, test_message: testMessage }),
    }),
  testEscalation: (alarmId: number) => apiRequest('/sites/test-alarm-escalation/', {
    method: 'POST',
    body: JSON.stringify({ alarm_id: alarmId }),
  }),
  testSummary: (notificationGroupId: number) => apiRequest('/sites/test-alarm-summary/', {
    method: 'POST',
    body: JSON.stringify({ notification_group_id: notificationGroupId }),
  }),
};

/**
 * Remote Alarm Management API
 */
export const remoteAlarmAPI = {
  /**
   * Receive alarm from remote SCADA system
   */
  receiveAlarm: async (alarmData: {
    site_id: number;
    tag_name: string;
    alarm_type: string;
    alarm_value: number;
    timestamp?: string;
    remote_system_id?: string;
    remote_alarm_id?: string;
  }): Promise<{
    message: string;
    alarm_id: number;
    created: boolean;
    tag_name: string;
    alarm_type: string;
    alarm_value: number;
    is_boolean_alarm: boolean;
    priority: string;
    timestamp: string;
  }> => {
    return apiRequest('/api/remote-alarms/receive/', {
      method: 'POST',
      body: JSON.stringify(alarmData)
    });
  },

  /**
   * Process SCADA tag data and create alarms
   */
  processSCADATag: async (tagData: {
    site_id: number;
    tag_name: string;
    current_value: number;
    data_quality: string;
  }): Promise<{
    message: string;
    alarm_id?: number;
    created?: boolean;
    alarm_type?: string;
    boolean_value?: boolean;
    current_value?: number;
    threshold?: number;
    tag_name?: string;
    data_quality?: string;
  }> => {
    return apiRequest('/api/remote-alarms/process-scada/', {
      method: 'POST',
      body: JSON.stringify(tagData)
    });
  },

  /**
   * Clear alarm on remote system
   */
  clearRemoteAlarm: async (alarmData: {
    site_id: number;
    tag_name: string;
    alarm_type: string;
  }): Promise<{
    message: string;
    alarm_id: number;
    tag_name: string;
    alarm_type: string;
    cleared_at: string;
  }> => {
    return apiRequest('/api/remote-alarms/clear/', {
      method: 'POST',
      body: JSON.stringify(alarmData)
    });
  }
};

export default {
  alarmDefinitions: alarmDefinitionsAPI,
  notificationGroups: alarmNotificationGroupsAPI,
  alarms: scadaAlarmsAPI,
  notifications: alarmNotificationsAPI,
  testEmail: testEmailServiceAPI,
  remoteAlarms: remoteAlarmAPI,
}; 